import React from "react";
import { Link } from "react-router-dom";

const CARLISLE_LOGO =
  "https://customer-assets.emergentagent.com/job_castel-new-look/artifacts/mphru06k_carlisle-logo-4c.svg";

const ServiceMarketingSection = ({ section, ctaLabel, contactTitle }) => {
  if (!section) return null;

  return (
    <section
      data-testid="service-marketing"
      className="bg-[var(--bg-dark)] text-white py-24 md:py-36 grain relative overflow-hidden"
    >
      <div className="container-page relative">
        {/* Eyebrow + Carlisle badge */}
        <div className="flex flex-wrap items-center gap-5 mb-12">
          <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.18em] text-white/50">
            <span className="w-8 h-px bg-white/40" />
            {section.eyebrow}
          </div>
          <span className="inline-flex items-center bg-white rounded-sm px-2.5 py-1.5">
            <img src={CARLISLE_LOGO} alt="CARLISLE®" className="h-5 w-auto" />
          </span>
        </div>

        {/* Lead intro */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="text-xl md:text-2xl text-white/85 leading-relaxed font-display">
              {section.leadIntro}
            </p>
          </div>
        </div>

        {/* Big pull-quote */}
        <blockquote className="mt-16 md:mt-20 max-w-5xl border-l-2 border-[var(--accent)] pl-8 md:pl-12">
          <p className="font-display text-3xl md:text-5xl leading-[1.05] tracking-[-0.02em] text-white">
            {section.bigQuote}
          </p>
        </blockquote>

        {/* Body paragraphs */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 space-y-6">
            {section.body.map((p, i) => (
              <p key={i} className="text-lg text-white/75 leading-relaxed">
                {p}
              </p>
            ))}
          </div>
          <div className="lg:col-span-5 lg:pl-8 lg:border-l border-white/15">
            <div className="font-display text-2xl md:text-3xl leading-tight text-white">
              {section.whyTitle}
            </div>
            <p className="mt-5 text-white/70 leading-relaxed">{section.whyIntro}</p>
          </div>
        </div>

        {/* Comparison table */}
        <div className="mt-16 md:mt-20 overflow-x-auto" data-testid="marketing-table">
          <table className="w-full min-w-[800px] border-t border-white/20">
            <thead>
              <tr>
                {section.tableHeaders.map((h, i) => (
                  <th
                    key={i}
                    className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 py-5 px-2 align-top border-b border-white/15"
                    style={{ width: i === 0 ? "22%" : i === 1 ? "33%" : "45%" }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.tableRows.map((row, i) => (
                <tr
                  key={i}
                  className="border-b border-white/10 hover:bg-white/[0.03] transition-colors"
                  data-testid={`marketing-row-${i}`}
                >
                  <td className="py-6 px-2 align-top">
                    <div className="font-display text-xl md:text-2xl text-white leading-tight">
                      {row.p}
                    </div>
                  </td>
                  <td className="py-6 px-2 align-top text-white/75 text-sm md:text-base leading-relaxed">
                    {row.a}
                  </td>
                  <td className="py-6 px-2 align-top text-white text-sm md:text-base leading-relaxed">
                    {row.b}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Closing */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h3 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-[-0.02em] text-white">
              {section.closingTitle}
            </h3>
            <p className="mt-8 text-lg text-white/75 leading-relaxed max-w-2xl">
              {section.closingBody}
            </p>
            <p className="mt-6 text-xl text-[var(--accent)] font-display max-w-2xl">
              {section.closingPunch}
            </p>
          </div>

          <div className="lg:col-span-5 lg:pl-8 lg:border-l border-white/15 flex flex-col justify-center">
            <p className="text-white/80 leading-relaxed mb-6">{section.ctaText}</p>
            <Link
              to="/#contact"
              data-testid="marketing-cta"
              className="group inline-flex items-center justify-between gap-3 bg-[var(--accent)] text-white px-6 py-4 hover:bg-white hover:text-black transition-colors"
            >
              <span>{ctaLabel}</span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceMarketingSection;
